import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MonthSelector, { currentMonthKey } from "@/components/MonthSelector";
import {
  FileUp,
  FileText,
  Download,
  Trash2,
  Loader2,
  Receipt,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function formatCLP(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  });
}

export default function AdminFacturasPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [file, setFile] = useState(null);
  const [folio, setFolio] = useState("");
  const [monto, setMonto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [filterClient, setFilterClient] = useState("ALL");
  const [uploadMes, setUploadMes] = useState(currentMonthKey());

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: u }] = await Promise.all([
        api.get("/facturas"),
        api.get("/admin/users"),
      ]);
      setItems(c);
      setClients(u.filter((x) => x.role === "client"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!selectedClient) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (!file) {
      toast.error("Selecciona el PDF de la factura");
      return;
    }
    if (!folio.trim()) {
      toast.error("Ingresa el folio");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mes", uploadMes);
    fd.append("folio", folio.trim());
    fd.append("monto", String(Number(monto) || 0));
    setUploading(true);
    try {
      await api.post(`/admin/clients/${selectedClient}/factura`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Factura subida correctamente");
      setOpenUpload(false);
      setFile(null);
      setFolio("");
      setMonto("");
      setSelectedClient("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/facturas/${r.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || `Factura_${r.folio || r.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/facturas/${id}`);
      toast.success("Factura eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const filtered =
    filterClient === "ALL"
      ? items
      : items.filter((r) => r.user_id === filterClient);

  const totalMonto = filtered.reduce((s, r) => s + Number(r.monto || 0), 0);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Facturación"
          title="Facturas Emitidas"
          description="Sube las facturas de servicio (PDF) que EXIS emite mensualmente a cada cliente. Ellos podrán visualizarlas y descargarlas desde su portal."
          actions={
            <button
              onClick={() => setOpenUpload(true)}
              data-testid="factura-upload-btn"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              <FileUp className="w-4 h-4" />
              Subir Factura
            </button>
          }
        />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="w-full md:w-72">
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger data-testid="factura-filter-client">
                <SelectValue placeholder="Filtrar por cliente" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="ALL">Todos los clientes</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social || c.name} — {c.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-2 bg-white border border-slate-200 px-4 py-2">
            <Receipt className="w-4 h-4 text-[#D95A40]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              Total emitido
            </span>
            <span className="font-display font-black text-[#0B1B3D] tabular-nums">
              {formatCLP(totalMonto)}
            </span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Cliente</Th>
                <Th>Archivo</Th>
                <Th>Folio</Th>
                <Th>Mes</Th>
                <Th className="text-right">Monto</Th>
                <Th className="text-right">Tamaño</Th>
                <Th>Subido</Th>
                <Th className="text-right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-slate-500">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-slate-500">
                    Aún no se han subido facturas.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`factura-row-${r.id}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B1B3D]">
                      {r.razon_social || r.user_name}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {r.user_email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-[#0B1B3D]">
                      <FileText className="w-4 h-4 text-red-500 shrink-0" />
                      <span className="truncate max-w-xs">{r.filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[#D95A40] font-semibold">
                    {r.folio || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 font-bold px-1.5 py-0.5 inline-block">
                      {r.mes || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0B1B3D]">
                    {formatCLP(r.monto)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-xs text-slate-600">
                    {((r.size_bytes || 0) / 1024).toFixed(1)} KB
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                    {new Date(r.uploaded_at).toLocaleString("es-CL")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => download(r)}
                        data-testid={`factura-download-${r.id}`}
                        className="p-2 bg-[#0B1B3D] hover:bg-[#D95A40] text-white transition-colors"
                        title="Descargar"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            data-testid={`factura-delete-${r.id}`}
                            className="p-2 hover:bg-red-50 text-slate-500 hover:text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              ¿Eliminar factura?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              El cliente perderá el acceso a este PDF.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(r.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload dialog */}
      <Dialog open={openUpload} onOpenChange={setOpenUpload}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tighter">
              Subir Factura Emitida
            </DialogTitle>
            <DialogDescription>
              Sube el PDF de la factura de servicio emitida por EXIS al cliente seleccionado. El cliente podrá descargarla desde su portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Cliente destino <span className="text-[#D95A40]">*</span>
              </label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="factura-select-client">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.razon_social || c.name} — {c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                  Mes <span className="text-[#D95A40]">*</span>
                </label>
                <MonthSelector
                  value={uploadMes}
                  onChange={setUploadMes}
                  testid="factura-mes-selector"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                  Folio <span className="text-[#D95A40]">*</span>
                </label>
                <input
                  type="text"
                  value={folio}
                  onChange={(e) => setFolio(e.target.value)}
                  data-testid="factura-folio-input"
                  placeholder="Ej. 1234"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#D95A40]"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Monto (CLP)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                data-testid="factura-monto-input"
                placeholder="0"
                className="w-full border border-slate-300 px-3 py-2 text-sm text-right tabular-nums focus:outline-none focus:border-[#D95A40]"
              />
              <div className="text-[10px] text-slate-400 mt-1">
                Solo visible en tu panel administrador.
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-1.5">
                Archivo PDF <span className="text-[#D95A40]">*</span>
              </label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="factura-file-input"
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0B1B3D] file:text-white file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-[#D95A40] file:cursor-pointer border border-slate-300 py-1"
              />
              {file && (
                <div className="mt-2 text-xs text-slate-600 flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2">
                  <FileText className="w-3.5 h-3.5 text-red-500" />
                  <span className="truncate">{file.name}</span>
                  <span className="text-slate-400">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setOpenUpload(false);
                setFile(null);
              }}
              className="px-4 py-2 border border-slate-300 text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={uploading || !file || !selectedClient || !folio.trim()}
              data-testid="factura-submit-btn"
              className="flex items-center gap-2 px-5 py-2 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white text-sm font-bold uppercase tracking-wider"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Subiendo...
                </>
              ) : (
                <>
                  <FileUp className="w-4 h-4" /> Subir
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
